<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import RealEstateCommissionBeneficiaryEditor from './RealEstateCommissionBeneficiaryEditor.vue';
import RealEstateCommissionGroupControls from './RealEstateCommissionGroupControls.vue';
import RealEstateCommissionOptionistReport from './RealEstateCommissionOptionistReport.vue';
import RealEstateCommissionSheetMenu from './RealEstateCommissionSheetMenu.vue';

const props = defineProps({
  activeGroup: { type: Object, required: true },
  activeGroupEnabled: { type: Boolean, default: true },
  activeGroupError: { type: Boolean, default: false },
  activeGroupQuantity: { type: Number, default: 0 },
  amountCents: { type: Number, default: 0 },
  canEdit: { type: Boolean, default: false },
  form: { type: Object, required: true },
  menuItems: { type: Array, default: () => [] },
  optionistReport: { type: String, default: '' },
  optionistReportExceeds: { type: Boolean, default: false },
  optionistReportTypes: { type: Array, default: () => [] },
  sheetLabel: { type: String, default: '' },
  title: { type: String, default: '' },
});

const emit = defineEmits([
  'add',
  'payment',
  'remove',
  'select-group',
  'select-recipient',
  'update',
  'update-enabled',
  'update-optionist-report',
  'update-quantity',
]);
const { t } = useI18n();
const activeRows = computed(() => props.form[props.activeGroup.id] || []);
</script>

<template>
  <div class="grid gap-5 lg:grid-cols-[16rem_minmax(0,1fr)]">
    <RealEstateCommissionSheetMenu
      :active="activeGroup.id"
      :items="menuItems"
      :sheet-label="sheetLabel"
      @select="emit('select-group', $event)"
    />

    <div class="grid content-start gap-5">
      <p
        v-if="activeGroupError"
        class="rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        role="alert"
      >
        {{ t('REAL_ESTATE_COMMISSION_INSTALLMENT.FIELDS.REQUIRED') }}
      </p>
      <RealEstateCommissionGroupControls
        :enabled="activeGroupEnabled"
        :quantity="activeGroupQuantity"
        :readonly="!canEdit"
        :show-quantity="!activeGroup.single"
        :toggle-label="activeGroup.toggleLabel"
        @update:enabled="emit('update-enabled', $event)"
        @update:quantity="emit('update-quantity', $event)"
      />

      <RealEstateCommissionOptionistReport
        v-if="activeGroup.id === 'corretores_opcionistas' && activeRows.length"
        :exceeds-limit="optionistReportExceeds"
        :readonly="!canEdit"
        :report-types="optionistReportTypes"
        :model-value="optionistReport"
        @update:model-value="emit('update-optionist-report', $event)"
      />

      <RealEstateCommissionBeneficiaryEditor
        v-if="activeGroupEnabled"
        :amount-cents="amountCents"
        :group="activeGroup.id"
        :options="activeGroup.options"
        :readonly="!canEdit"
        :rows="activeRows"
        :single="activeGroup.single"
        :title="title"
        @add="emit('add')"
        @payment="emit('payment', $event)"
        @remove="emit('remove', $event)"
        @select="(index, optionId) => emit('select-recipient', index, optionId)"
        @update="(index, field, value) => emit('update', index, field, value)"
      />
    </div>
  </div>
</template>
